/**
 * Cancelling an order that was already rung up.
 *
 * WHY A REASON IS REQUIRED
 * ------------------------
 * A cancel takes a sale off the books, and that is the oldest way to make cash
 * disappear from a till — ring up, take the money, cancel, pocket the
 * difference. Every cancel is kept, named with who did it and why, and counted
 * on the day-close, so the reason is not paperwork: it is what turns a pile of
 * cancels into something an owner can read.
 *
 * A few common reasons are one tap; anything else is typed. The cancel cannot
 * go through with nothing said.
 */
import { useState } from 'react';
import { formatPKR } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';
import { ManagerApproval } from './ManagerApproval.jsx';
import { needsApproval } from './approvalCodes.js';

const QUICK = ['Rung up by mistake', 'Wrong item', 'Customer changed their mind'];

export function CancelOrderDialog({ order, busy, error, onConfirm, onSendRequest, onClose, sending }) {
  const [reason, setReason] = useState('');

  const where = order?.table_number
    ? `Table ${order.table_number}`
    : order?.order_type === 'delivery'
      ? 'Delivery'
      : 'Takeaway';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Cancel this order"
    >
      <div className="w-full max-w-sm rounded-xl border border-rule bg-surface p-5">
        <h2 className="font-semibold text-lg text-ink">Cancel this order?</h2>
        <p className="mt-1 text-sm text-ink-2">
          {where} · {order?.order_number} · {formatPKR(order?.total_amount ?? 0)}
        </p>
        <p className="mt-2 text-xs text-ink-3">
          It is recorded as cancelled, not deleted, and the kitchen is told to stop.
        </p>

        <span className="mt-4 mb-1.5 block text-sm font-medium text-ink-2">Why?</span>
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              aria-pressed={reason === r}
              className={`rounded-lg border px-2.5 py-1.5 text-sm ${
                reason === r
                  ? 'border-brand-400 bg-brand-400/15 text-brand-300'
                  : 'border-rule text-ink-2 hover:text-ink'
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="or type a reason"
          aria-label="Reason for cancelling"
          className="mt-2.5 h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />

        {/*
          A manager's PIN, when the branch requires one to cancel. The refusal
          comes back after the cashier has typed a reason and pressed the button,
          so the box opens here in place — the reason they typed is kept and sent
          again with the PIN attached.
        */}
        {needsApproval(error) ? (
          <>
            <ManagerApproval
              error={error}
              busy={busy}
              onApprove={(approval) => onConfirm(reason.trim(), approval)}
            />
            {/* Or, when no manager is standing here: send it up for one to
                approve on their own device. The cancel does not happen now — it
                waits on the approvals screen, and this bill shows "waiting" on
                the board until it is decided. */}
            {onSendRequest && (
              <button
                type="button"
                onClick={() => onSendRequest(reason.trim())}
                disabled={sending || !reason.trim()}
                className="mt-2 w-full rounded-lg border border-rule px-3 py-2 text-sm font-medium text-ink-2 hover:bg-surface-3 disabled:opacity-40"
              >
                {sending ? 'Sending…' : 'Or send to a manager to approve'}
              </button>
            )}
          </>
        ) : (
          error && (
            <p role="alert" className="mt-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">
              {error.message}
            </p>
          )
        )}

        <div className="mt-4 flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
            Keep it
          </Button>
          {!needsApproval(error) && (
            <Button
              className="flex-1 !bg-bad hover:!brightness-110"
              busy={busy}
              disabled={!reason.trim()}
              onClick={() => onConfirm(reason.trim())}
            >
              Cancel the order
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
